import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';

import styles from './index.scss';
import { find, orderBy } from 'lodash';
import {
  ageFormatter,
  convertToFormattedMoney,
  convertToFormattedNumber,
  getCustomerHighestLabel,
  putHyphenIfEmpty
} from '../../../../services/util';
import SemiCircularBar from '../../../../components/SemiCircularBar';
import { fillColorForPredictionLevel } from '../../../../util';
import NameValueTile from '../../../../components/NameValueTile';

/**
 * CustomerAttributeRow component
 * @param clusterAttributes
 * @param isExpanded
 * @returns {*}
 * @constructor
 */
const CustomerAttributeRow = ({
  clusterDescriptionCodes,
  clusterInformation,
  isExpanded
}) => {
  if (clusterInformation) {
    const { attributes } = clusterInformation;
    let riskScoreLabel = 'Risk Score';
    let riskScore = '-';
    let customerType = '-';
    let customerAge = '-';
    let customerTypeLabel = 'Customer Type';
    let customerSegmentLabel = 'Customer Segment';
    let formattedCustomerSegment = '-';
    let customerAgeLabel = 'Customer age';
    let formattedCustomerType = '-';
    if (
      Array.isArray(clusterDescriptionCodes) &&
      clusterDescriptionCodes.length > 0
    ) {
      clusterDescriptionCodes.forEach((featureCode) => {
        attributes.forEach((clusterSegment) => {
          if (
            featureCode.code === 'RISK_SCORE' &&
            'RISK_SCORE' === clusterSegment.name
          ) {
            riskScoreLabel = 'Risk Score';
            riskScore = putHyphenIfEmpty(clusterSegment.feature_value);
          }
          if (
            featureCode.code === 'PARTY_AGE' &&
            'PARTY_AGE' === clusterSegment.name
          ) {
            customerAgeLabel = 'Customer Age';
            customerAge = ageFormatter(clusterSegment.feature_value);
          }
          if (
            featureCode.code === 'INDIVIDUAL_CORPORATE_TYPE' &&
            'INDIVIDUAL_CORPORATE_TYPE' === clusterSegment.name
          ) {
            customerTypeLabel = 'Customer Type';
            customerType = clusterSegment.feature_value;
            formattedCustomerType = getCustomerHighestLabel(customerType);
          }
          if (
            featureCode.code === 'CUSTOMER_SEGMENT_CODE' &&
            'CUSTOMER_SEGMENT_CODE' === clusterSegment.name
          ) {
            customerSegmentLabel = 'Customer Segment';
            formattedCustomerSegment = getCustomerHighestLabel(
              clusterSegment.feature_value
            );
          }
        });
      });
    }
    return (
      <div style={{ display: 'flex' }}>
        <div className={styles.containerTitle} style={{ width: 250 }}>
          Customer Attributes
        </div>
        <div style={{ width: '100%' }}>
          <div className={styles.flexEvenly}>
            {/* Risk Score */}
            <div
              style={{ width: 200, padding: 10 }}
              className={styles.flexEvenly}>
              <div style={{ marginRight: 10 }}>{riskScoreLabel}</div>
              {riskScore !== '-' && riskScore && (
                <SemiCircularBar
                  value={Number(riskScore).toFixed(2)}
                  color={fillColorForPredictionLevel(riskScore)}
                  thickness={10}
                  min={0}
                  max={100}
                  text="Score"
                  showMin
                  showMax
                  showText
                />
              )}
              {riskScore === '-' && <div style={{ fontWeight: 'bold' }}>-</div>}
            </div>

            {/* Customer Type */}
            <div
              style={{ width: 200, padding: 10 }}
              className={styles.flexEvenly}>
              <div style={{ marginRight: 10 }}>{customerTypeLabel}</div>
              <div style={{ fontWeight: 'bold', wordBreak: 'break-word' }}>
                {formattedCustomerType === 'I'
                  ? 'Individual'
                  : formattedCustomerType === 'C'
                  ? 'Corporate'
                  : formattedCustomerType}
              </div>
            </div>

            {/* Customer Segment */}
            <div
              style={{ width: 200, padding: 10 }}
              className={styles.flexEvenly}>
              <div style={{ marginRight: 10 }}>{customerSegmentLabel}</div>
              <div style={{ fontWeight: 'bold', wordBreak: 'break-word' }}>
                {formattedCustomerSegment}
              </div>
            </div>

            {/* Customer Age */}
            <div
              style={{ width: 200, padding: 10 }}
              className={styles.flexEvenly}>
              <div style={{ marginRight: 10 }}>{customerAgeLabel}</div>
              <div style={{ fontWeight: 'bold', wordBreak: 'break-word' }}>
                {customerAge}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return null;
};
/**
 * HistoricalRiskRow component
 * @param historicalAttributes
 * @param clusterDescriptionCodes
 * @returns {*}
 * @constructor
 */
const HistoricalRiskRow = ({
  clusterDescriptionCodes,
  historicalAttributes
}) => {
  const {
    historicalAlertCount = '-',
    historicalStrCount = '-'
  } = historicalAttributes;
  let historicalAlertCountLabel = 'ALERT_90DAY_COUNT_CUSTOMER';
  let historicalStrCountLabel = 'STR_90DAY_COUNT_CUSTOMER';
  if (
    Array.isArray(clusterDescriptionCodes) &&
    clusterDescriptionCodes.length > 0
  ) {
    clusterDescriptionCodes.forEach((clusterCode) => {
      if (
        clusterCode?.code &&
        clusterCode.code === 'ALERT_90DAY_COUNT_CUSTOMER'
      ) {
        historicalAlertCountLabel = clusterCode.description;
      }

      if (
        clusterCode?.code &&
        clusterCode.code === 'STR_90DAY_COUNT_CUSTOMER'
      ) {
        historicalStrCountLabel = clusterCode.description;
      }
    });
  }
  return (
    <div style={{ display: 'flex' }}>
      <div className={styles.containerTitle} style={{ width: 250 }}>
        Historical Risk
      </div>
      <div style={{ width: '100%' }}>
        <div className={styles.flexEvenly}>
          <div
            style={{ padding: 10, marginRight: 20 }}
            className={styles.flexEvenly}>
            <div style={{ marginRight: 10 }}>{historicalAlertCountLabel}:</div>
            <div style={{ fontWeight: 'bold' }}>{historicalAlertCount}</div>
          </div>
          <div
            style={{ padding: 10, marginRight: 20 }}
            className={styles.flexEvenly}>
            <div style={{ marginRight: 10 }}>{historicalStrCountLabel}</div>
            <div style={{ fontWeight: 'bold' }}>{historicalStrCount}</div>
          </div>
        </div>
      </div>
    </div>
  );
};

/**
 * ClusterInformation component
 */
export class ClusterInformation extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      isCollapsed: true
    };
  }

  onToggleCollapse = () => {
    const { isCollapsed } = this.state;
    this.setState({
      isCollapsed: !isCollapsed
    });
  };

  render() {
    const { clusterViewDescription, isLoading } = this.props;
    const { fetched, data } = clusterViewDescription || {};
    let clusterViewCodes = [];
    if (fetched) {
      clusterViewCodes = data.data;
    }

    const { isCollapsed } = this.state;
    const { clusterInformation } = this.props;
    const { clusterId = '-' } = clusterInformation;
    if (!fetched && isLoading) {
      return (
        <div className={classnames('row', styles.clusterInformationWrapper)}>
          <div className="col-sm-12">
            <div className={styles.clusterInformationContainer}>
              <div className="row">
                <div className="col-sm-6">
                  <span className={styles.containerTitle}>
                    Loading Cluster Information...
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      );
    }

    if (
      clusterInformation &&
      Array.isArray(clusterInformation.attributes) &&
      clusterInformation.attributes.length > 0
    ) {
      const attributes = clusterInformation.attributes;
      const filteredAttributes = attributes.filter((attribute) => {
        const { name = '-' } = attribute || {};
        if (
          name === 'RISK_SCORE' ||
          name === 'PARTY_AGE' ||
          name === 'INDIVIDUAL_CORPORATE_TYPE' ||
          name === 'CUSTOMER_SEGMENT_CODE'
        ) {
          return false;
        }
        return !name.endsWith('_CLUSTER_SCORE');
      });
      const orderedAttributes = isCollapsed
        ? orderBy(filteredAttributes, ['feature_value'], ['desc']).slice(0, 3)
        : orderBy(filteredAttributes, ['feature_value'], ['desc']);
      const formattedAttributes = orderedAttributes.map((attribute, idx) => {
        let { feature_value = '-', name = '-', comparison = '-' } =
          attribute || {};
        const descriptionObject = find(clusterViewCodes, { code: name });
        if (descriptionObject) {
          name = descriptionObject.description;
        }

        if (feature_value !== '-') {
          if (name.toLowerCase().indexOf('amount') !== -1) {
            feature_value = convertToFormattedMoney(feature_value);
          } else {
            feature_value = convertToFormattedNumber(feature_value);
          }
        }

        return (
          <NameValueTile
            idx={idx}
            name={name}
            score={feature_value}
            comparison={comparison}
          />
        );
      });

      let collapseClass = isCollapsed ? 'fa fa-angle-down' : 'fa fa-angle-up';

      return (
        <div className={classnames('row', styles.clusterInformationWrapper)}>
          <div className="col-sm-12">
            <div className="row">
              <div className="col-sm-11">
                <div
                  style={{
                    fontSize: 14,
                    fontWeight: 500,
                    color: '#000000'
                  }}>
                  Cluster View
                </div>
              </div>
              <div className="col-sm-1">
                <div
                  onClick={this.onToggleCollapse}
                  className={styles.collapseButton}>
                  <i className={collapseClass} />
                </div>
              </div>
            </div>

            <div className="col-sm-12" style={{ textAlign: 'right' }}>
              <div className={styles.clusterLegendsContainer}>
                <div className={styles.legendItem}>
                  <span className={classnames(styles.legendSymbol, styles.red)}>
                    &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
                  </span>
                  &nbsp;
                  <span className="label-text">High</span>
                </div>

                <div className={styles.legendItem}>
                  <span
                    className={classnames(styles.legendSymbol, styles.yellow)}>
                    &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
                  </span>
                  &nbsp;
                  <span className="label-text">Medium</span>
                </div>

                <div className={styles.legendItem}>
                  <span
                    className={classnames(styles.legendSymbol, styles.blue)}>
                    &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
                  </span>
                  &nbsp;
                  <span className="label-text">Low</span>
                </div>
              </div>
            </div>

            <div
              style={{
                display: 'flex',
                paddingTop: 10,
                paddingBottom: 10
              }}>
              <div className={styles.containerTitle} style={{ width: 220 }}>
                Cluster Code
              </div>
              <div
                style={{
                  marginRight: 10,
                  fontWeight: 'bold'
                }}>
                {clusterId}
              </div>
            </div>

            <CustomerAttributeRow
              clusterDescriptionCodes={clusterViewCodes}
              isExpanded={false}
              clusterInformation={clusterInformation}
            />

            <div style={{ marginTop: '15px' }} />

            <HistoricalRiskRow
              clusterDescriptionCodes={clusterViewCodes}
              historicalAttributes={clusterInformation}
            />

            <div style={{ marginTop: '15px' }} />

            <div className={styles.clusterInformationContainer}>
              <div className="row col-sm-12">
                <div className={styles.containerTitle}>
                  Transaction Behaviour
                </div>
                <div className="row" style={{ marginLeft: 200 }}>
                  {formattedAttributes}
                </div>
              </div>
            </div>
          </div>
        </div>
      );
    }

    if (
      fetched &&
      clusterInformation &&
      Array.isArray(clusterInformation.attributes) &&
      clusterInformation.attributes.length === 0
    ) {
      return (
        <div className={classnames('row', styles.clusterInformationWrapper)}>
          <div className="col-sm-12">
            <div className={styles.clusterInformationContainer}>
              <div className="row">
                <div className="col-sm-2">
                  <span className={styles.containerTitle}>
                    No data to display.
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      );
    }
    return <div />;
  }
}

/**
 * Proptypes
 * @type {{isLoading: shim, clusterInformation: shim}}
 */
ClusterInformation.propTypes = {
  clusterInformation: PropTypes.object,
  isLoading: PropTypes.bool,
  clusterViewDescription: PropTypes.object
};
